import { ChangeEvent, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNotification } from "@shared/contexts/NotificationContext";
import { redeemRecoveryCode } from "@services/api";
import {
  listMfaFactors,
  MfaFactor,
  supabase,
  verifyMfa,
} from "@services/supabaseAPI";
import { stepUpManager } from "@shared/utils/stepUpManager";

type Mode = "totp" | "recovery";

/**
 * Mid-session step-up dialog. Opens when an API call returns 401
 * mfa_required (via stepUpManager), or when the client pre-flights a
 * sensitive action (e.g. password change) and finds the session at
 * AAL1.
 *
 * Distinct from MfaChallengeForm (which lives in the landing-page
 * modal and drives the initial sign-in bootstrap):
 *   - No bootstrap on success. The user is already in the app.
 *   - Cancel just rejects the pending request; it does NOT sign the
 *     user out.
 *   - Mounted at the app root so it's available from anywhere.
 */
const StepUpDialog = () => {
  const { showSuccess } = useNotification();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("totp");
  const [factor, setFactor] = useState<MfaFactor | null>(null);
  const [loadingFactor, setLoadingFactor] = useState(true);
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Subscribe to the manager. Reset form state on open so a second
  // step-up in the same session doesn't reuse stale errors / inputs.
  useEffect(() => {
    const unsub = stepUpManager.subscribe((nextOpen) => {
      setOpen(nextOpen);
      if (nextOpen) {
        setMode("totp");
        setCode("");
        setRecovery("");
        setErrorMsg("");
        setSubmitting(false);
        setLoadingFactor(true);
      }
    });
    return unsub;
  }, []);

  // Fetch the user's factor whenever we open. Listing on every open
  // (not once at mount) covers the case where the user enrolled or
  // unenrolled in the same session.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const factors = await listMfaFactors();
      if (cancelled) return;
      setFactor(factors[0] ?? null);
      setLoadingFactor(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleVerifyTotp = async () => {
    if (!factor) return;
    setSubmitting(true);
    setErrorMsg("");
    const result = await verifyMfa(factor.id, code.trim());
    setSubmitting(false);
    if (!result.success) {
      setErrorMsg(result.error || "Incorrect code");
      setCode("");
      return;
    }
    stepUpManager.resolve(true);
  };

  const handleRedeem = async () => {
    setSubmitting(true);
    setErrorMsg("");
    const result = await redeemRecoveryCode(recovery);
    if (!result.success) {
      setErrorMsg(result.error.message || "Recovery code not recognised");
      setRecovery("");
      setSubmitting(false);
      return;
    }
    // Factor is now removed server-side; refresh the SDK session
    // before resolving so the replayed request carries the updated
    // (no-factor) state. The replay will succeed at AAL1 because
    // RequireAAL2 only triggers when factors exist on the user.
    await supabase.auth.refreshSession();
    setSubmitting(false);
    showSuccess(
      "Recovery code used. Two-factor authentication has been removed — re-enroll from your profile when you're ready."
    );
    stepUpManager.resolve(true);
  };

  const handleCancel = () => {
    stepUpManager.resolve(false);
  };

  return (
    <Dialog
      open={open}
      // Cancel via backdrop / Esc is equivalent to clicking Cancel.
      // No silent dismissal: the pending request always sees a
      // definite outcome.
      onClose={handleCancel}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Confirm with two-factor</DialogTitle>
      <DialogContent>
        {loadingFactor ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !factor && mode === "totp" ? (
          <Alert severity="warning">
            No authenticator factor is enrolled on your account. Use a recovery
            code to continue, or sign out and re-enroll.
          </Alert>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {mode === "totp"
                ? "This action requires you to verify with your authenticator."
                : "Paste one of the recovery codes you saved when you enabled 2FA. The code will be consumed and 2FA will be reset."}
            </Typography>

            {mode === "totp" ? (
              <TextField
                autoFocus
                label="Authenticator code"
                value={code}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                error={!!errorMsg}
                helperText={errorMsg || " "}
                fullWidth
              />
            ) : (
              <TextField
                autoFocus
                label="Recovery code"
                placeholder="XXXX-XXXX-XXXX-XXXX"
                value={recovery}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setRecovery(e.target.value)
                }
                error={!!errorMsg}
                helperText={errorMsg || " "}
                fullWidth
              />
            )}

            <Link
              component="button"
              type="button"
              onClick={() => {
                setErrorMsg("");
                setMode(mode === "totp" ? "recovery" : "totp");
              }}
              sx={{ alignSelf: "flex-start" }}
            >
              {mode === "totp" ? "Use a recovery code" : "Use my authenticator"}
            </Link>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={submitting}>
          Cancel
        </Button>
        {!loadingFactor && (factor || mode === "recovery") && (
          <Button
            variant="contained"
            onClick={mode === "totp" ? handleVerifyTotp : handleRedeem}
            disabled={
              submitting ||
              (mode === "totp" ? code.length !== 6 : recovery.trim().length === 0)
            }
          >
            {submitting ? "Verifying…" : mode === "totp" ? "Verify" : "Use code"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default StepUpDialog;
